import React, { useState } from 'react';
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Filter, AlertCircle, XCircle, Eye, EyeOff, ChevronDown, ChevronUp, Settings } from 'lucide-react';

interface ScrapesFiltersProps {
  showErrorsOnly: boolean;
  onShowErrorsOnlyChange: (value: boolean) => void;
  showInvalidOnly: boolean;
  onShowInvalidOnlyChange: (value: boolean) => void;
  showUnverifiedOnly: boolean;
  onShowUnverifiedOnlyChange: (value: boolean) => void;
  hideNoPricing: boolean;
  onHideNoPricingChange: (value: boolean) => void;
  errorCount: number;
  invalidCount: number;
  unverifiedCount: number;
  noPricingCount: number;
  totalCount: number;
  filteredCount: number;
}

export const ScrapesFilters: React.FC<ScrapesFiltersProps> = ({
  showErrorsOnly,
  onShowErrorsOnlyChange,
  showInvalidOnly,
  onShowInvalidOnlyChange,
  showUnverifiedOnly,
  onShowUnverifiedOnlyChange,
  hideNoPricing,
  onHideNoPricingChange,
  errorCount,
  invalidCount,
  unverifiedCount,
  noPricingCount,
  totalCount,
  filteredCount,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const basicActiveFilters = [showErrorsOnly, showInvalidOnly, showUnverifiedOnly].filter(Boolean).length;
  const advancedActiveFilters = [!hideNoPricing].filter(Boolean).length;
  const totalActiveFilters = basicActiveFilters + advancedActiveFilters;
  
  const FilterToggle = ({ 
    id, 
    checked, 
    onChange, 
    label, 
    count, 
    icon: Icon, 
    variant = "secondary" 
  }: {
    id: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    count: number;
    icon: React.ComponentType<{ className?: string }>;
    variant?: "secondary" | "destructive" | "outline";
  }) => (
    <div 
      className={`flex items-center gap-2 p-2 rounded-lg border transition-all cursor-pointer hover:bg-gray-50 ${
        checked ? 'bg-blue-50 border-blue-200' : 'border-gray-200'
      }`}
      onClick={() => onChange(!checked)}
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(checked) => onChange(checked as boolean)}
        className="pointer-events-none"
      />
      <Icon className={`h-4 w-4 ${
        checked ? 'text-blue-600' : 'text-gray-500'
      }`} />
      <Label htmlFor={id} className="text-sm font-medium cursor-pointer flex-1">
        {label}
      </Label>
      <Badge 
        variant={checked ? "default" : "secondary"} 
        className="text-xs px-2 py-0.5"
      >
        {count}
      </Badge>
    </div>
  );

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-700">Filters</h3>
          {totalActiveFilters > 0 && (
            <Badge variant="outline" className="text-xs">
              {totalActiveFilters} active
            </Badge>
          )}
        </div>
        <div className="text-sm text-gray-500">
          <span className="font-medium">{filteredCount}</span>
          <span className="text-gray-400 ml-1">of {totalCount} total</span>
        </div>
      </div>
      
      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FilterToggle
          id="show-errors-only"
          checked={showErrorsOnly}
          onChange={onShowErrorsOnlyChange}
          label="Errors Only"
          count={errorCount}
          icon={XCircle}
        />
        
        <FilterToggle
          id="show-invalid-only"
          checked={showInvalidOnly}
          onChange={onShowInvalidOnlyChange}
          label="Invalid Only"
          count={invalidCount}
          icon={AlertCircle}
        />
        
        <FilterToggle
          id="show-unverified-only"
          checked={showUnverifiedOnly}
          onChange={onShowUnverifiedOnlyChange}
          label="Unverified Only"
          count={unverifiedCount}
          icon={Eye}
        />
      </div>
      
      {/* Advanced Filters Toggle */}
      <div className="mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <Settings className="h-4 w-4" />
          <span>Advanced Filters</span>
          {advancedActiveFilters > 0 && (
            <Badge variant="secondary" className="text-xs">
              {advancedActiveFilters}
            </Badge>
          )}
          {showAdvanced ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
        
        {showAdvanced && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FilterToggle
                id="hide-no-pricing"
                checked={hideNoPricing}
                onChange={onHideNoPricingChange}
                label="Hide No Pricing"
                count={noPricingCount}
                icon={EyeOff}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
